import React, { useEffect, useRef } from 'react'
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import {
  LabraChatProvider,
  LabraAIChatPanel,
  openLabraChat,
  sendLabraChatPrompt,
} from '@labrav/common'
import type { FundingTodo } from '@labrav/funding'
import { useDispatch, useSelector, store } from '../../../store'
import { useSalesforceContext } from '../contexts/SalesforceContext'
import { SfActionType } from '../types/salesforce'
import { useCanvasContentResize } from '../hooks/useCanvasContentResize'
import { FundingOutletContext } from './FundingLayout'
import { chatServiceUrl } from '../../../common/utils/constants'
import { useStyles } from './FundingChatWrapper.styles'
import { setOktaUserId } from '../../../common/modules/userProfile/action'
import { FundingAgentTodosHeader } from '../components/FundingAgentTodosHeader/FundingAgentTodosHeader'

/**
 * Salesforce Canvas route for the funding chat, navigated to inline from the
 * todos tile within the same iframe. Mounts `<LabraAIChatPanel>` with the
 * funding header in the default compact side-panel layout (no expand toggle),
 * and resizes the host frame via `useCanvasContentResize`. When `prompt` is on
 * the URL it seeds the opening message from a clicked todo or typed query.
 * Closing the panel (Redux `isPanelOpen` → false) navigates back to
 * `/funding/todos` so the todos tile is restored. We watch Redux rather than an
 * `onClose` prop because the published `@labrav/common` panel does not yet
 * expose that callback.
 */
export const FundingChatWrapper: React.FC = () => {
  const { classes } = useStyles()
  const { partnerId, actions } = useSalesforceContext()
  const { userId = '' } = useParams<{ userId?: string }>()
  const navigate = useNavigate()
  const rootRef = useCanvasContentResize<HTMLDivElement>(560)
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const prompt = searchParams.get('prompt') ?? ''

  const isPanelOpen = useSelector(
    state => state.labraChat?.isPanelOpen ?? false
  )
  const hasOpenedRef = useRef(false)

  const { todos, loading } = useOutletContext<FundingOutletContext>()

  const handleTodoSelect = (todo: FundingTodo) => {
    if (todo.action === SfActionType.OpenSfCosell) {
      actions.triggerAction(todo.action)
      return
    }
    dispatch(
      sendLabraChatPrompt(partnerId, todo.prompt ?? todo.text, {
        displayMessage: todo.text,
      })
    )
  }

  useEffect(() => {
    if (isPanelOpen) {
      hasOpenedRef.current = true
      return
    }
    if (hasOpenedRef.current) {
      navigate({ pathname: '../todos' }, { relative: 'path' })
    }
  }, [isPanelOpen, navigate])

  useEffect(() => {
    if (!partnerId) return
    // The chat send reads `state.userProfile.userProfile.id` for `user_id`,
    // which Canvas never loads (no profile fetch behind the JWT). Seed it from
    // the injected route param so chat/session/history requests carry the user.
    if (userId) dispatch(setOktaUserId(userId))

    dispatch(openLabraChat())

    // Seed the conversation from the clicked todo / typed query, but only when
    // the thread is still empty so a re-mount can't resend it.
    if (prompt && (store.getState().labraChat?.messages?.length ?? 0) === 0) {
      dispatch(sendLabraChatPrompt(partnerId, prompt))
    }
  }, [partnerId, userId, dispatch, prompt])

  return (
    <LabraChatProvider
      partnerId={partnerId}
      accountId={partnerId}
      userId={userId}
      chatEndpoint={chatServiceUrl}
      agentType="supervisor_agent"
    >
      <div ref={rootRef} className={classes.root}>
        <LabraAIChatPanel
          title="Labra AI Agent"
          header={
            <FundingAgentTodosHeader
              todos={todos}
              loading={loading}
              onSelect={handleTodoSelect}
            />
          }
          hideFullScreenToggle
          showPanelBorder
          variant="embedded"
        />
      </div>
    </LabraChatProvider>
  )
}

export default FundingChatWrapper
